<template>
  <div>
    <div class="mb-4">
      <h2 class="text-base font-semibold text-gray-800">Word Library</h2>
      <p class="text-sm text-gray-500 mt-1">Manage individual words within each dictionary. Words are used to classify
        and validate business names during name search.</p>
    </div>
    <div class="grid grid-cols-12 gap-4">
      <AppModal v-model="showAddWord" title="Add a Word">
        <div class="col-span-12">
          <form class="w-full space-y-3">
            <input
              type="text"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              v-model="form.word"
              placeholder="Word"
              required
            />
            <input
              type="text"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              v-model="form.description"
              placeholder="Description"
              required
            />

            <select v-model="form.position"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
              <option v-for="(option, idx) in positionList" :key="idx" :value="option.value">{{ option.text }}</option>
            </select>

            <select v-model="form.dictionaryId"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
              <option v-for="(option, idx)  in dictionaryList" :key="idx" :value="option.value">{{ option.text }}</option>
            </select>

            <div class="flex justify-end">
              <button
                type="button"
                @click="saveWord"
                class="btn-primary">
                Save
              </button>
            </div>
          </form>
        </div>
      </AppModal>
    </div>
    <div class="grid grid-cols-12 gap-4">
      <AppModal v-model="showEditWord" title="Edit Word">
        <div class="col-span-12">
          <form class="w-full space-y-3">
            <input
              type="text"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              v-model="eform.word"
              placeholder="Word"
              required
            />
            <input
              type="text"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              v-model="eform.description"
              placeholder="Description"
              required
            />

            <select v-model="eform.position"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
              <option v-for="(option, idx) in positionList" :key="idx" :value="option.value">{{ option.text }}</option>
            </select>

            <select v-model="eform.dictionaryId"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
              <option v-for="(option, idx) in dictionaryList" :key="idx" :value="option.value">{{ option.text }}</option>
            </select>

            <p class="text-sm mt-3 italic text-blue">
              This action will be logged and your account flagged as one who took this action and also had the
              permission to do so.
            </p>

            <div class="flex justify-end">
              <button
                type="button"
                @click="updateWord"
                class="btn-primary">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </AppModal>
    </div>
    <div class="grid grid-cols-12 gap-4">
      <AppModal v-model="showDeleteWord" hide-footer title="Remove Word">
        <div class="col-span-12">
          <form class="w-full">
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-12">
                <p class="text-center">
                  Are you sure you want to remove this word <b>{{ dform.word.toUpperCase() }}</b> from the dictionary in
                  the URSB database?<br/><br/>
                  Removing this word means that it will no longer be used and considered as a restriction during <b>Name
                  Reservation</b>.
                </p>
                <p class="text-sm mt-3 italic text-red">
                  This action will be logged and your account flagged as one who took this action and also had the
                  permission to do so.
                </p>
              </div>
            </div>
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-4"></div>
              <div class="col-span-4 text-center justify-content-center">
                <button
                  type="button"
                  @click="removeWord"
                  class="btn-sm-danger w-full mt-3">
                  Yes
                </button>
              </div>
              <div class="col-span-4"></div>
            </div>
          </form>
        </div>
      </AppModal>
    </div>

    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-12 mb-4">
        <div class="grid grid-cols-12 gap-4 mx-1">
          <div class="col-span-2">
            <button
              class="btn-primary w-full">
              <i class="fa fa-plus"></i>
              Add Word
            </button>
          </div>
          <div class="col-span-3">
            <select v-model="filter.dictionaryId"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
              <option v-for="(option, idx) in dictionaryList" :key="idx" :value="option.value">{{ option.text }}</option>
            </select>
          </div>
          <div class="col-span-4"></div>
          <div class="col-span-3">
            <input type="text"
                placeholder="Search"
                required
                   class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                v-on:keyup.enter="searchDictionary(form.search_text)"/>
          </div>
        </div>
      </div>

    </div>

    <div class="mx-auto" v-if="loading">
      <div class="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
        <div class="bg-primary h-full animate-pulse" style="width: 100%"></div>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-4 mx-auto">
      <div class="col-span-12">
        <table class="w-full leading-normal">
          <thead>
            <tr class="border-b border-t border-gray-200">
              <th class="t-header">#</th>
              <th class="t-header">Name</th>
              <th class="t-header">Description</th>
              <th class="t-header">Position</th>
              <th class="t-header">Dictionary</th>
              <th v-if="canEditWord()" class="t-header">Edit</th>
              <th v-if="canDeleteWord()" class="t-header">Remove</th>
            </tr>
          </thead>
          <tbody>
            <tr :class="[(idx%2) ? rows.even : rows.odd]" v-for="(page, idx) in getWords" :key="idx">
              <td class="t-body">{{ idx + 1 }}</td>
              <td class="t-body font-bold">{{ page.word }}</td>
              <td class="t-body">{{ page.description }}</td>
              <td class="t-body">{{ page.position }}</td>
              <td class="t-body text-green italic">{{ page.dictionary.name }}</td>
              <td v-if="canEditWord()" class="t-body">
                <button @click="editWordPrompt(page)" class="btn-sm-primary">
                  <i class="fa fa-pencil mr-1"></i>Edit
                </button>
              </td>
              <td v-if="canDeleteWord()" class="t-body">
                <button @click="deleteWordPrompt(page)" class="btn-sm-danger">
                  <i class="fa fa-ban mr-1"></i> De-activate
                </button>
              </td>
            </tr>
            <tr>
              <td v-if="getWords && getWords.length == 0" colspan="8" class="bg-inertgrey text-center py-1">
                <span class="text-sm">No results found</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../stores";
import AppModal from "@/components/ui/AppModal.vue";

interface DictionaryOption {
  text: string
  value: string | null
}

interface PositionOption {
  text: string
  value: string | null
}

interface WordForm {
  word: string | null
  dictionaryId: string | null
  description: string | null
  position: string | null
  search_text?: string
}

interface DeleteForm {
  id: string | null
  word: string
}

interface Dictionary {
  id: string
  name: string
}

interface Word {
  id: string
  word: string
  description: string
  position: string
  dictionary: Dictionary
}

interface GlobalProperties {
  $can?: (permission: string, group: string) => boolean
  successToast?: (title: string, message: string) => void
  errorToast?: (title: string, message: string) => void
}

const store = useConfigurationsStore();
const instance = getCurrentInstance()
const proxy = instance?.proxy as unknown as GlobalProperties | undefined

// Reactive state
const showAddWord = ref(false)
const showEditWord = ref(false)
const showDeleteWord = ref(false)
const loading = ref(true)
const selectedId = ref<string | null>(null)
const dictionaryList = ref<DictionaryOption[]>([])

const positionList = ref<PositionOption[]>([
  { text: "--Word Position--", value: null },
  { value: 'Start', text: 'Start' },
  { value: 'Any', text: 'Any' },
  { value: 'End', text: 'End' },
])

const filter = ref({
  dictionaryId: null as string | null,
})

const form = ref<WordForm>({
  word: null,
  dictionaryId: null,
  description: null,
  position: null,
})

const eform = ref<WordForm>({
  word: null,
  dictionaryId: null,
  description: null,
  position: null,
})

const dform = ref<DeleteForm>({
  id: null,
  word: ""
})

const rows = ref({
  odd: "border-t border-gray-200 hover:bg-gray-10",
  even: "border-t border-gray-200 bg-gray-10"
})

// Computed
const getWords = computed(() => store.words || []);
const getDictionaries = computed(() => store.dictionaries);
const getWordTransactionError = computed(() => store.wordError);
const getWordTransactionSuccess = computed(() => store.wordSuccess);
const getFilter = computed(() => filter.value.dictionaryId)

// Methods
const canEditWord = () => {
  return proxy?.$can?.('can_edit_dictionary_entry', 'general') ?? false
}

const canDeleteWord = () => {
  return proxy?.$can?.('can_delete_dictionary_entry', 'general') ?? false
}

const editWordPrompt = (data: Word) => {
  selectedId.value = data.id
  eform.value = {
    word: data.word,
    description: data.description,
    dictionaryId: data.dictionary.id,
    position: data.position
  }
  showEditWord.value = true
}

const deleteWordPrompt = (data: Word) => {
  dform.value = {
    id: data.id,
    word: data.word
  }
  showDeleteWord.value = true
}

const saveWord = () => {
  loading.value = true
  store.addWord(form.value);
}

const updateWord = () => {
  loading.value = true
  const data = {
    id: selectedId.value,
    form: eform.value
  }
  store.editWord(data);
}

const removeWord = () => {
  loading.value = true
  store.deleteWord(dform.value);
}

const searchDictionary = (_searchText?: string) => {
  // Search implementation
}

const filterDictionaryInfo = (data: Dictionary[]): DictionaryOption[] => {
  const dataObj: DictionaryOption[] = [{ text: "--Filter by Dictionary--", value: null }]
  data.forEach(obj => {
    dataObj.push({
      text: obj.name,
      value: obj.id
    })
  })
  return dataObj
}

const filterWords = () => {
  loading.value = true
  store.fetchFilteredWords(filter.value.dictionaryId ?? "");
}

// Lifecycle
onMounted(() => {
  store.fetchDictionaries();
  store.fetchWords();
})

// Watchers
watch(getWords, () => {
  loading.value = false
})

watch(getWordTransactionSuccess, () => {
  showAddWord.value = false
  showEditWord.value = false
  showDeleteWord.value = false
  store.fetchWords();

  form.value = {
    word: null,
    dictionaryId: null,
    description: null,
    position: null
  }

  proxy?.successToast?.("Success", "Successfully completed")
})

watch(getWordTransactionError, (message: any) => {
  loading.value = false
  proxy?.errorToast?.("Error", message?.data?.message || "An error occurred")
})

watch(getFilter, (id) => {
  if (id === "null" || id === null) {
    store.fetchWords();
  } else {
    store.fetchFilteredWords(id);
  }
})

watch(getDictionaries, (data) => {
  if (data) {
    dictionaryList.value = filterDictionaryInfo(data)
  }
})
</script>
